import { useAxios, useSubjectMember } from '@healthblocks-io/core'
import { useState } from 'react'

import { getBlobFromUri } from '@/modules/shared/helpers/file-upload'
import { showLocalPreview } from './useStoredMedicationFiles'

import type { MedicationFileAttachment } from '@/modules/app/types/HealthParameter.types'

/** A photo that is taken but not stored yet, because the patient has not confirmed it. */
export type CapturedMedicationPhoto = { url: string; takenAt: string }

/**
 * Medication photos are shown to the care team, so they belong in blob storage
 * rather than whichever driver happens to be the project's first. Displaying
 * them relies on that driver handing out SAS links; without those,
 * useStoredMedicationFiles falls back to streaming each photo.
 */
const MEDICATION_PHOTO_DRIVER_TYPE = 'azure'

/**
 * Request body for storing a medication photo through `POST /file`.
 *
 * The api refuses a file without an owner, and only accepts the subject the
 * request is made for as that owner.
 */
export const toMedicationPhotoFormData = (photo: Blob, memberId: number): FormData => {
  const body = new FormData()
  body.append('file', photo, 'medication.jpg')
  body.append('memberId', memberId.toString())
  body.append('driverType', MEDICATION_PHOTO_DRIVER_TYPE)

  return body
}

/** Stores medication photos the patient confirmed. */
export function useMedicationPhotoUpload() {
  const axios = useAxios()
  const member = useSubjectMember()
  const [isUploading, setIsUploading] = useState(false)

  /**
   * Stores a photo and returns its reference, or null when that failed.
   *
   * On success the photo's object URL is handed to the thumbnails as a preview
   * until its stored url is in. On failure the caller keeps it, so the photo
   * stays on screen for a retry.
   */
  const uploadPhoto = async (photo: CapturedMedicationPhoto): Promise<MedicationFileAttachment | null> => {
    if (!member?.id) {
      return null
    }

    setIsUploading(true)
    try {
      const response = await axios.post<{ id: number }>(
        '/file',
        toMedicationPhotoFormData(await getBlobFromUri(photo.url), member.id),
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      const fileId = String(response.data.id)
      showLocalPreview(fileId, photo.url)

      return { fileId, takenAt: photo.takenAt }
    } catch (cause) {
      console.error('Error uploading medication photo', cause)

      return null
    } finally {
      setIsUploading(false)
    }
  }

  return { uploadPhoto, isUploading }
}
